import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Stepper } from '@enamelai/design-system'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  checkPortabilityAction,
  resubmitPortRequestDbAction,
  submitPortRequestDbAction,
  verifyPortOtpAction,
} from '@store/actions/settings-communication.action'
import './PortNumberStyle.css'
import {
  IClinicNumber,
  IPortDetailsStep2,
  ITwilioPortRequest,
} from '@dataTypes/clinic.type'
import {
  EnterDetails,
  OTPVerification,
  ReviewInfo,
  SelectNumber,
} from './Steps'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import {
  getPortRequestDeclineInfo,
  toPortRequestClinicSnapshot,
} from '@utils/portRequestPayload.util'
import PortRequestDeclineBanner from '../../shared/PortRequestDeclineBanner.component'
import { usePhoneManagementCtx } from '../PhoneManagement.context'

const defaultNumberData: IClinicNumber = {
  clinic_number_id: '',
  clinic_id: '',
  is_verified: false,
  is_portable: false,
  phone_number: '',
  phone_carrier: '',
  number_sid: '',
  status: '',
  purchased_date: '',
  released_date: '',
  released_reason: '',
}

const defaultDetailsStep2Data: IPortDetailsStep2 = {
  full_name: '',
  email_address: '',
  phone_number: '',
  wireless_account_number: '',
  pin_or_ssn: '',
  billing_statement_file: null,
}

type Props = {
  show: boolean
  onHide: () => void
  initialPhone?: string
  resubmitRequest?: ITwilioPortRequest | null
}

const PortNumberModal = ({
  show,
  onHide,
  initialPhone = '',
  resubmitRequest = null,
}: Props): React.ReactElement => {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<number>(0)
  const [otp, setOtp] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [numberData, setNumberData] = useState<IClinicNumber>(defaultNumberData)
  const [detailsStep2Data, setDetailsStep2Data] = useState<IPortDetailsStep2>(
    defaultDetailsStep2Data,
  )
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [nonPortableNumberHint, setNonPortableNumberHint] = useState<
    string | null
  >(null)
  const [otpNumberList, setOtpNumberList] = useState<string[]>([])
  const [currentOtpIndex, setCurrentOtpIndex] = useState(0)
  const [verifiedOtpNumbers, setVerifiedOtpNumbers] = useState<string[]>([])
  const [existingDocumentId, setExistingDocumentId] = useState<string | null>(
    null,
  )
  const knownNonPortableNumbersRef = useRef<Set<string>>(new Set())
  const { loading, error } = useAppSelector(
    (state) => state.settingsCommunication,
  )
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { runAsync } = usePhoneManagementCtx()

  const isResubmitMode = !!resubmitRequest
  const declineInfo = useMemo(
    () => (resubmitRequest ? getPortRequestDeclineInfo(resubmitRequest) : null),
    [resubmitRequest],
  )
  const steps = isResubmitMode
    ? ['Update number', 'Update details', 'Review & resubmit']
    : ['Select number', 'Enter details', 'Verification', 'Review info']
  const reviewStepIndex = isResubmitMode ? 2 : 3
  const existingDocumentLabel = existingDocumentId
    ? 'Billing statement on file'
    : null

  useEffect(() => {
    if (!show) return
    setStep(0)
    setValidationErrors({})
    setNonPortableNumberHint(null)
    setOtp(null)
    setOtpNumberList([])
    setCurrentOtpIndex(0)
    setVerifiedOtpNumbers([])
    knownNonPortableNumbersRef.current.clear()

    if (resubmitRequest) {
      const numbers = (resubmitRequest.phone_numbers || []).map((number) =>
        formatPhoneNumber(normalizePhoneNumber(number), false),
      )
      setNumberData({
        ...defaultNumberData,
        clinic_id: resubmitRequest.clinic_id,
        phone_number: numbers.join('\n'),
        phone_carrier: resubmitRequest.phone_carrier || '',
        is_portable: true,
      })
      setDetailsStep2Data({
        full_name: resubmitRequest.representative_name || '',
        email_address: resubmitRequest.representative_email || '',
        phone_number: resubmitRequest.wireless_phone_number || '',
        wireless_account_number: resubmitRequest.wireless_account_number || '',
        pin_or_ssn: resubmitRequest.wireless_pin_or_ssn || '',
        billing_statement_file: null,
      })
      setExistingDocumentId(resubmitRequest.twilio_port_document_id || null)
      return
    }

    setNumberData({
      ...defaultNumberData,
      phone_number: initialPhone,
    })
    setDetailsStep2Data(defaultDetailsStep2Data)
    setExistingDocumentId(null)
  }, [show, initialPhone, resubmitRequest])

  const reset = (): void => {
    setStep(0)
    setNumberData(defaultNumberData)
    setDetailsStep2Data(defaultDetailsStep2Data)
    setExistingDocumentId(null)
    setValidationErrors({})
    setNonPortableNumberHint(null)
    setOtp(null)
    setOtpNumberList([])
    setCurrentOtpIndex(0)
    setVerifiedOtpNumbers([])
    knownNonPortableNumbersRef.current.clear()
  }

  const handleClose = (): void => {
    reset()
    onHide()
  }

  const isValidPhoneNumber = (value: string): boolean => {
    const normalized = value.replace(/\D/g, '')
    return (
      normalized.length === 10 ||
      (normalized.length === 11 && normalized.startsWith('1'))
    )
  }

  const phoneNumberFieldError = (value: string): string | undefined => {
    const lines = value
      .split('\n')
      .map((item) => item.trim())
      .filter((item) => item !== '')
    const numberList = lines.map((item) => normalizePhoneNumber(item))
    if (!value.trim() || numberList.length === 0) return 'Input phone number'
    if (numberList.some((item) => !isValidPhoneNumber(item))) {
      return 'Invalid phone number'
    }
    return undefined
  }

  const validateForm = useCallback(async (): Promise<boolean> => {
    const errors: { [key: string]: string } = {}
    switch (step) {
      case 0: {
        const lines = numberData.phone_number
          .split('\n')
          .map((item) => item.trim())
          .filter((item) => item !== '')
        const numberList = lines.map((item) => normalizePhoneNumber(item))
        if (
          !numberData.phone_number ||
          numberData.phone_number.trim() === '' ||
          numberList.length === 0
        ) {
          errors.phone_number = 'Input phone number'
        }
        if (numberList.some((item) => !isValidPhoneNumber(item))) {
          errors.phone_number = 'Invalid phone number'
        }
        if (
          !numberData.phone_carrier ||
          numberData.phone_carrier.trim() === ''
        ) {
          errors.phone_carrier = 'Select phone carrier'
        }
        if (Object.keys(errors).length > 0) {
          setNonPortableNumberHint(null)
          setValidationErrors(errors)
          return false
        }
        const cachedFailIndex = numberList.findIndex((number) =>
          knownNonPortableNumbersRef.current.has(number),
        )
        if (cachedFailIndex >= 0) {
          const lineAsEntered =
            lines[cachedFailIndex] ?? numberList[cachedFailIndex]
          setNonPortableNumberHint(lineAsEntered)
          setValidationErrors({
            is_portable: `${numberList[cachedFailIndex]} is already marked as not portable.`,
          })
          setNumberData((prev) => ({
            ...prev,
            is_portable: false,
          }))
          return false
        }
        setBusy(true)
        try {
          const portability = await dispatch(
            checkPortabilityAction(numberList),
          ).unwrap()
          if (!portability || portability.length !== numberList.length) {
            setNonPortableNumberHint(null)
            setValidationErrors({
              is_portable: 'Failed to check portability.',
            })
            setNumberData((prev) => ({
              ...prev,
              is_portable: false,
            }))
            return false
          }
          const failIndex = portability.findIndex(
            (p) => p && p.notPortableReasonCode,
          )
          if (failIndex >= 0) {
            const p = portability[failIndex]
            const displayNum = numberList[failIndex]
            const reason =
              p.notPortableReason?.trim() ||
              String(p.notPortableReasonCode ?? '')
            const lineAsEntered = lines[failIndex] ?? displayNum
            knownNonPortableNumbersRef.current.add(displayNum)
            setNonPortableNumberHint(lineAsEntered)
            setValidationErrors({
              is_portable: `${displayNum}: ${reason}`,
            })
            setNumberData((prev) => ({
              ...prev,
              is_portable: false,
              notPortableReasonCode:
                p.notPortableReasonCode != null
                  ? String(p.notPortableReasonCode)
                  : null,
            }))
            return false
          }
          setNonPortableNumberHint(null)
          setNumberData((prev) => ({
            ...prev,
            is_portable: true,
            notPortableReasonCode: null,
          }))
          break
        } catch (e: unknown) {
          const message =
            typeof e === 'string'
              ? e
              : e instanceof Error
                ? e.message
                : 'Failed to check portability.'
          setNonPortableNumberHint(null)
          setValidationErrors({ is_portable: message })
          setNumberData((prev) => ({ ...prev, is_portable: false }))
          return false
        } finally {
          setBusy(false)
        }
      }
      case 1: {
        if (detailsStep2Data.full_name?.trim() === '') {
          errors.full_name = 'Full name is required'
        }
        if (detailsStep2Data.email_address?.trim() === '') {
          errors.email_address = 'Email address is required'
        }
        if (
          detailsStep2Data.billing_statement_file === null &&
          !existingDocumentId
        ) {
          errors.billing_statement_file = 'Billing statement file is required'
        }
        const hasWirelessGroupValue =
          !!detailsStep2Data.phone_number?.trim() ||
          !!detailsStep2Data.wireless_account_number?.trim() ||
          !!detailsStep2Data.pin_or_ssn?.trim()
        if (hasWirelessGroupValue) {
          if (!detailsStep2Data.phone_number?.trim()) {
            errors.phone_number = 'Phone number is required'
          }
          if (!isValidPhoneNumber(detailsStep2Data.phone_number?.trim())) {
            errors.phone_number = 'Invalid phone number'
          }
          if (!detailsStep2Data.wireless_account_number?.trim()) {
            errors.wireless_account_number =
              'Wireless account number is required'
          }
          if (!detailsStep2Data.pin_or_ssn?.trim()) {
            errors.pin_or_ssn = 'Pin/ SSN is required'
          }
        }
        break
      }
      case 2: {
        break
      }
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [
    detailsStep2Data.billing_statement_file,
    detailsStep2Data.email_address,
    detailsStep2Data.full_name,
    detailsStep2Data.phone_number,
    detailsStep2Data.pin_or_ssn,
    detailsStep2Data.wireless_account_number,
    existingDocumentId,
    numberData.phone_carrier,
    numberData.phone_number,
    step,
  ])

  const handleChange = (
    field: string,
    value: string | boolean | null,
  ): void => {
    const updatedData: Partial<IClinicNumber> = { [field]: value }

    if (field === 'phone_number' && typeof value === 'string') {
      updatedData.is_portable = false
      setNonPortableNumberHint(null)
      const nextPhoneError = phoneNumberFieldError(value)
      setValidationErrors((prev) => {
        if (!prev.phone_number && !prev.is_portable) return prev
        const next = { ...prev }
        delete next.is_portable
        if (nextPhoneError) {
          next.phone_number = nextPhoneError
        } else {
          delete next.phone_number
        }
        return next
      })
    }
    if (field === 'phone_carrier') {
      const carrier = String(value ?? '').trim()
      setValidationErrors((prev) => {
        if (!prev.phone_carrier) return prev
        if (!carrier) {
          return { ...prev, phone_carrier: 'Select phone carrier' }
        }
        const next = { ...prev }
        delete next.phone_carrier
        return next
      })
    }
    if (field === 'is_verified') {
      void validateForm()
    }
    setNumberData((prev) => ({ ...prev, ...updatedData }))
    if (step !== 0 && Object.keys(validationErrors).length) void validateForm()
  }

  const handleWirelessNumberChange = (
    field: string,
    value: string | File,
  ): void => {
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors({})
    }
    setDetailsStep2Data((prev) => ({ ...prev, [field]: value }))
  }

  useEffect(() => {
    if (otp === null || otp.length !== 6) return
    setValidationErrors((prev) => {
      if (!prev.is_verified) return prev
      const next = { ...prev }
      delete next.is_verified
      return next
    })
  }, [otp])

  const verifyOtp = async (): Promise<void> => {
    const currentPhoneNumber = otpNumberList[currentOtpIndex]
    if (!currentPhoneNumber) {
      setValidationErrors((prev) => ({
        ...prev,
        is_verified: 'Phone number for verification is missing',
      }))
      return
    }
    try {
      if (otp === null || otp.length !== 6) {
        setValidationErrors((prev) => ({
          ...prev,
          is_verified: 'OTP verification failed',
        }))
        return
      }
      const response = await dispatch(
        verifyPortOtpAction({
          phoneNumber: currentPhoneNumber,
          otp,
        }),
      ).unwrap()

      if (!response.isVerified) {
        setValidationErrors((prev) => ({
          ...prev,
          is_verified: 'OTP verification failed',
        }))
        return
      }

      const hasCurrentVerified = verifiedOtpNumbers.includes(currentPhoneNumber)
      const totalVerified = hasCurrentVerified
        ? verifiedOtpNumbers.length
        : verifiedOtpNumbers.length + 1
      setVerifiedOtpNumbers((prev) =>
        prev.includes(currentPhoneNumber)
          ? prev
          : [...prev, currentPhoneNumber],
      )
      setValidationErrors((prev) => {
        if (!prev.is_verified) return prev
        const next = { ...prev }
        delete next.is_verified
        return next
      })
      setOtp(null)
      if (totalVerified < otpNumberList.length) {
        setCurrentOtpIndex((prev) => prev + 1)
        return
      }

      handleChange('is_verified', true)
      setStep(3)
    } catch {
      setValidationErrors((prev) => ({
        ...prev,
        is_verified: 'OTP verification failed',
      }))
    }
  }

  const handleNext = async (): Promise<void> => {
    const nextStep = step + 1
    if (!isResubmitMode && step === 2) {
      await verifyOtp()
      return
    }
    if (await validateForm()) {
      if (!isResubmitMode && nextStep === 2) {
        const numbers = numberData.phone_number
          .split('\n')
          .map((item) => normalizePhoneNumber(item))
          .filter((item) => item.trim() !== '')
        setOtpNumberList(numbers)
        setCurrentOtpIndex(0)
        setVerifiedOtpNumbers([])
        setOtp(null)
        setValidationErrors((prev) => {
          if (!prev.is_verified) return prev
          const next = { ...prev }
          delete next.is_verified
          return next
        })
      }
      setStep(nextStep)
    }
  }

  const handlePrevious = (): void => setStep(step - 1)

  const getSectionComponent = (): React.ReactElement => {
    if (step === reviewStepIndex) {
      return (
        <ReviewInfo
          numberData={numberData}
          detailsStep2Data={detailsStep2Data}
          existingDocumentLabel={existingDocumentLabel}
        />
      )
    }

    switch (step) {
      case 0:
        return (
          <SelectNumber
            numberData={numberData}
            handleChange={handleChange}
            nonPortableNumberHint={nonPortableNumberHint}
            validationErrors={validationErrors}
          />
        )
      case 1:
        return (
          <EnterDetails
            data={detailsStep2Data}
            handleChange={handleWirelessNumberChange}
            validationErrors={validationErrors}
            existingDocumentLabel={existingDocumentLabel}
          />
        )
      case 2:
        return (
          <OTPVerification
            numberData={numberData}
            handleChange={handleChange}
            currentPhoneNumber={otpNumberList[currentOtpIndex] || ''}
            currentIndex={currentOtpIndex}
            totalCount={otpNumberList.length || 1}
            setOtp={setOtp}
            validationErrors={validationErrors}
          />
        )
      default:
        return <></>
    }
  }

  const handleProceedSave = async (): Promise<void> => {
    const clinicId = selectedClinic?.clinic_id || numberData.clinic_id
    const clinicName = selectedClinic?.clinic_name || ''
    const billingStatementFile = detailsStep2Data.billing_statement_file
    const numberList = numberData.phone_number
      .split('\n')
      .map((item) => normalizePhoneNumber(item))
      .filter((item) => item.trim() !== '')

    if (
      !clinicId ||
      numberList.length === 0 ||
      (!billingStatementFile && !existingDocumentId)
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        is_portable: 'Missing clinic, phone number, or billing statement data.',
      }))
      return
    }

    if (!toPortRequestClinicSnapshot(selectedClinic)) {
      setValidationErrors((prev) => ({
        ...prev,
        is_portable:
          'Clinic address details are required. Update clinic settings and try again.',
      }))
      return
    }

    if (isResubmitMode && resubmitRequest?.twilio_port_request_id) {
      const succeeded =
        (await runAsync(
          () =>
            dispatch(
              resubmitPortRequestDbAction({
                portRequestId: resubmitRequest.twilio_port_request_id!,
                clinicId,
                clinicName,
                clinicSnapshot: toPortRequestClinicSnapshot(selectedClinic),
                billingStatementFile,
                existingTwilioPortDocumentId: existingDocumentId,
                phone_numbers: numberList,
                phone_carrier: numberData.phone_carrier || '',
                representative_name: detailsStep2Data.full_name || '',
                representative_email: detailsStep2Data.email_address || '',
                wireless_phone_number: detailsStep2Data.phone_number || null,
                wireless_account_number:
                  detailsStep2Data.wireless_account_number || null,
                wireless_pin_or_ssn: detailsStep2Data.pin_or_ssn || null,
              }),
            ).unwrap(),
          {
            successTitle: 'Port request resubmitted',
            successMessage:
              'Your corrected port request was sent back for review.',
            errorTitle: 'Could not resubmit port request',
            errorFallbackMessage: 'Failed to resubmit port request.',
          },
        )) !== undefined
      if (succeeded) {
        handleClose()
      }
      return
    }

    if (!billingStatementFile) {
      setValidationErrors((prev) => ({
        ...prev,
        is_portable: 'Missing clinic, phone number, or billing statement data.',
      }))
      return
    }

    const result = await runAsync(
      () =>
        dispatch(
          submitPortRequestDbAction({
            clinicId,
            clinicName,
            clinicSnapshot: toPortRequestClinicSnapshot(selectedClinic),
            billingStatementFile,
            phone_numbers: numberList,
            phone_carrier: numberData.phone_carrier || '',
            representative_name: detailsStep2Data.full_name || '',
            representative_email: detailsStep2Data.email_address || '',
            wireless_phone_number: detailsStep2Data.phone_number || null,
            wireless_account_number:
              detailsStep2Data.wireless_account_number || null,
            wireless_pin_or_ssn: detailsStep2Data.pin_or_ssn || null,
          }),
        ).unwrap(),
      {
        successTitle: 'Port request submitted',
        successMessage: 'Your port request was saved successfully.',
        errorTitle: 'Could not submit port request',
        errorFallbackMessage: 'Failed to save port request.',
      },
    )
    if (result !== undefined) {
      handleClose()
    }
  }

  const isReviewStep = step === steps.length - 1
  const isErrorStep = step === steps.length && !!error
  const primaryLabel = isErrorStep
    ? 'Close'
    : isReviewStep
      ? isResubmitMode
        ? 'Resubmit'
        : 'Proceed'
      : 'Next'
  const secondaryLabel = isErrorStep
    ? 'Retry'
    : step === 0
      ? 'Cancel'
      : 'Previous'

  return (
    <Modal
      open={show}
      onClose={handleClose}
      closeOnBackdrop={false}
      className="scp-pm-port-modal"
      size="md"
      title={isResubmitMode ? 'Resubmit phone port request' : 'Port number'}
      primaryLabel={primaryLabel}
      secondaryLabel={secondaryLabel}
      primaryDisabled={
        isErrorStep
          ? false
          : isReviewStep
            ? loading || busy
            : step === 0
              ? loading || busy
              : Object.keys(validationErrors).length > 0 ||
                (!isResubmitMode && step === 2 && !otp)
      }
      onPrimaryClick={() => {
        if (isErrorStep) {
          handleClose()
          return
        }
        if (isReviewStep) {
          void handleProceedSave()
          return
        }
        void handleNext()
      }}
      onSecondaryClick={() => {
        if (isErrorStep) {
          setStep(0)
          return
        }
        if (step === 0) {
          handleClose()
          return
        }
        handlePrevious()
      }}
    >
      {step < steps.length ? (
        <Stepper steps={steps} currentStep={step} />
      ) : null}
      {isResubmitMode && declineInfo ? (
        <PortRequestDeclineBanner declineInfo={declineInfo} />
      ) : null}
      <div className="w-100" key={step}>
        {getSectionComponent()}
      </div>
    </Modal>
  )
}

export default PortNumberModal
